import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/Logo_laboratorio_simple_verde-removebg-preview.png';
import img1 from '../assets/cdc-XLhDvfz0sUM-unsplash.jpg';
import img2 from '../assets/indra-projects-L1ip8dEWuzE-unsplash.jpg';
import img3 from '../assets/national-cancer-institute-XknuBmnjbKg-unsplash.jpg';
import img4 from '../assets/nguy-n-hi-p-ufwC2cmbaaI-unsplash.jpg';
import img5 from '../assets/ryan-zazueta--lulun7F32c-unsplash.jpg';
import styles from './LandingPage.module.css';
import { homeForRole } from '../utils/roleHome';

const HERO_IMAGES = [img1, img2, img3, img4, img5];

const ANALISIS_CATEGORIAS = [
  {
    nombre: 'Hematología',
    icono: '🩸',
    items: ['Hemograma completo', 'Reticulocitos', 'Eritrosedimentación (VSG)', 'Hematocrito'],
  },
  {
    nombre: 'Bioquímica',
    icono: '🧪',
    items: ['Glucosa / Glucemia', 'Urea', 'Creatinina', 'Ácido úrico', 'Colesterol total', 'HDL / LDL', 'Triglicéridos', 'Hierro sérico', 'Ferritina', 'Hemoglobina glicosilada (HbA1c)'],
  },
  {
    nombre: 'Hepatograma',
    icono: '🔬',
    items: ['TGO / TGP', 'Fosfatasa alcalina (FAL)', 'GGT', 'Bilirrubina total y directa', 'Proteínas totales y albúmina', 'Proteinograma electroforético'],
  },
  {
    nombre: 'Endocrinología y Hormonas',
    icono: '⚗️',
    items: ['TSH', 'T3 libre / T4 libre', 'Insulina', 'Cortisol', 'FSH / LH', 'Testosterona', 'Prolactina', 'Estradiol'],
  },
  {
    nombre: 'Serología e Inmunología',
    icono: '🛡️',
    items: ['VDRL / FTA-Abs', 'HIV', 'Chagas (HAI)', 'Hepatitis B (HBsAg, Anti-HBs)', 'Hepatitis C', 'Toxoplasmosis', 'Factor reumatoide', 'PCR cuantitativa', 'ASO'],
  },
  {
    nombre: 'Ionograma y Minerales',
    icono: '⚡',
    items: ['Sodio', 'Potasio', 'Cloro', 'Calcio total e iónico', 'Fósforo', 'Magnesio'],
  },
  {
    nombre: 'Orina y Función Renal',
    icono: '💧',
    items: ['Orina completa', 'Microalbuminuria', 'Proteinuria 24 hs', 'Creatinuria', 'Depuración de creatinina', 'Urocultivo'],
  },
  {
    nombre: 'Coagulación',
    icono: '🩹',
    items: ['Tiempo de protrombina (TP / RIN)', 'KPTT', 'Fibrinógeno', 'Dímero D', 'Tiempo de trombina'],
  },
  {
    nombre: 'Microbiología y Cultivos',
    icono: '🦠',
    items: ['Hemocultivo', 'Urocultivo', 'Coprocultivo', 'Cultivo de secreciones', 'Antibiograma'],
  },
  {
    nombre: 'Linfocitos',
    icono: '🔵',
    items: ['CD4 totales y porcentaje', 'CD8 totales y porcentaje', 'Subpoblaciones linfocitarias'],
  },
  {
    nombre: 'Lupus y Autoinmunidad',
    icono: '🧬',
    items: ['Anticoagulante lúpico (LA1 / LA2)', 'ANA', 'Anti-DNA', 'Anticuerpos antifosfolípidos'],
  },
];

const PREPARACION_PASOS = [
  {
    titulo: 'Ayuno',
    descripcion: 'La mayoría de los análisis de sangre requieren ayuno de 8 a 12 horas. Durante ese período solo se permite tomar agua natural.',
    icono: '⏱️',
  },
  {
    titulo: 'Sin alcohol',
    descripcion: 'No consumas bebidas alcohólicas al menos 48 horas antes de la extracción, ya que pueden alterar los resultados.',
    icono: '🚫',
  },
  {
    titulo: 'Actividad física',
    descripcion: 'Evitá el ejercicio intenso 24 horas antes del análisis. El esfuerzo físico puede modificar valores como CK, glucosa o ácido úrico.',
    icono: '🏃',
  },
  {
    titulo: 'Muestra de orina',
    descripcion: 'Usá la primera orina de la mañana y descartá el primer chorro. Recolectá en un frasco limpio y seco. Entregá dentro de las 2 horas.',
    icono: '💧',
  },
  {
    titulo: 'Medicamentos',
    descripcion: 'Consultá con tu médico si debés tomar o suspender alguna medicación antes del análisis. Algunos fármacos pueden interferir en los resultados.',
    icono: '💊',
  },
  {
    titulo: 'Documentación',
    descripcion: 'Traé tu pedido médico, documento de identidad y, si tenés obra social, la credencial y número de afiliado.',
    icono: '📋',
  },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  function handleCta() {
    if (isAuthenticated) {
      navigate(homeForRole(user?.role));
    } else {
      window.open('/login', '_blank');
    }
  }

  const [heroIndex, setHeroIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setHeroIndex((i) => (i + 1) % HERO_IMAGES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  function prevSlide() {
    setHeroIndex((i) => (i - 1 + HERO_IMAGES.length) % HERO_IMAGES.length);
  }

  function nextSlide() {
    setHeroIndex((i) => (i + 1) % HERO_IMAGES.length);
  }

  function scrollTo(id: string) {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  }

  return (
    <div className={styles.page}>
      {/* ── Navbar ── */}
      <header className={styles.navbar}>
        <div className={styles.navbarInner}>
          <div className={styles.navbarBrand}>
            <img src={logo} alt="Logo" className={styles.navLogo} />
            <span className={styles.navTitle}>Laboratorio Sagrado Corazón</span>
          </div>
          <nav className={styles.navLinks}>
            <button className={styles.navLink} onClick={() => scrollTo('contacto')}>Contacto</button>
            <button className={styles.navLink} onClick={() => scrollTo('preparacion')}>Preparación</button>
            <button className={styles.navLink} onClick={() => scrollTo('analisis')}>Análisis</button>
          </nav>
          <button className={styles.navCta} onClick={handleCta}>
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="currentColor" style={{ verticalAlign: 'middle', marginRight: 6 }}>
              <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/>
            </svg>
            {isAuthenticated ? 'Ir al sistema' : 'Portal paciente'}
          </button>
        </div>
      </header>

      {/* ── Hero Carrusel ── */}
      <section className={styles.hero}>
        {HERO_IMAGES.map((img, i) => (
          <div
            key={i}
            className={`${styles.heroSlide} ${i === heroIndex ? styles.heroSlideActive : ''}`}
            style={{ backgroundImage: `url(${img})` }}
          />
        ))}
        <div className={styles.heroOverlay} />

        <div className={styles.heroContent}>
          <p className={styles.heroSub}>Laboratorio de análisis clínicos</p>
          <h1 className={styles.heroTitle}>Sagrado Corazón</h1>
          <p className={styles.heroDesc}>
            Más de 20 años brindando un servicio de calidad.
          </p>
          <div className={styles.heroActions}>
            <button className={styles.heroBtnPrimary} onClick={() => scrollTo('contacto')}>
              Ver horarios y contacto
            </button>
            <button className={styles.heroBtnSecondary} onClick={() => scrollTo('analisis')}>
              Listado de análisis
            </button>
          </div>
        </div>

        <button className={styles.carouselPrev} onClick={prevSlide} aria-label="Anterior">&#8249;</button>
        <button className={styles.carouselNext} onClick={nextSlide} aria-label="Siguiente">&#8250;</button>

        <div className={styles.scrollIndicator} onClick={() => scrollTo('contacto')}>
          <span className={styles.scrollIndicatorText}>Deslizá para ver más</span>
          <svg className={styles.scrollIndicatorArrow} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </section>

      {/* ── Contacto ── */}
      <section id="contacto" className={styles.section}>
        <div className={styles.sectionInner}>
          <h2 className={styles.sectionTitle}>Contactanos</h2>
          <p className={styles.sectionSubtitle}>
            Encontranos en el corazón del barrio Cofico.
          </p>

          <div className={styles.contactGrid}>
            <div className={styles.contactCards}>
              <div className={styles.contactCard}>
                <div className={styles.contactCardIcon}>📍</div>
                <div>
                  <h3 className={styles.contactCardTitle}>Dirección</h3>
                  <p className={styles.contactCardText}>Roque Saenz Peña 1377</p>
                  <p className={styles.contactCardText}>Barrio Cofico, Córdoba</p>
                </div>
              </div>

              <div className={styles.contactCard}>
                <div className={styles.contactCardIcon}>📞</div>
                <div>
                  <h3 className={styles.contactCardTitle}>Teléfonos</h3>
                  <p className={styles.contactCardText}>
                    <a href="tel:+543514715788" className={styles.contactLink}>351-7686490</a>
                    <span className={styles.contactCardHint}> — Turnos</span>
                  </p>
                </div>
              </div>

              <div className={styles.contactCard}>
                <div className={styles.contactCardIcon}>✉️</div>
                <div>
                  <h3 className={styles.contactCardTitle}>Email</h3>
                  <p className={styles.contactCardText}>
                    <a href="mailto:labsagradocorazon1967@gmail.com" className={styles.contactLink}>
                      labsagradocorazon1967@gmail.com
                    </a>
                  </p>
                </div>
              </div>

              <div className={styles.contactCard}>
                <div className={styles.contactCardIcon}>🕐</div>
                <div>
                  <h3 className={styles.contactCardTitle}>Horarios</h3>
                  <p className={styles.contactCardText}>Lun a Vie: 7:00 – 13:00 hs - Con turno previo</p>
                </div>
              </div>
            </div>

            <div className={styles.mapWrapper}>
              <iframe
                title="Ubicación Laboratorio Sagrado Corazón"
                src="https://maps.google.com/maps?q=Roque+Saenz+Pe%C3%B1a+1377,+C%C3%B3rdoba,+Argentina&t=&z=15&ie=UTF8&iwloc=&output=embed"
                className={styles.map}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Preparación del Paciente ── */}
      <section id="preparacion" className={`${styles.section} ${styles.sectionAlt}`}>
        <div className={styles.sectionInner}>
          <h2 className={styles.sectionTitle}>Preparación del paciente</h2>
          <p className={styles.sectionSubtitle}>
            Para obtener resultados precisos, seguí estas indicaciones antes de concurrir al laboratorio.
          </p>
          <div className={styles.prepGrid}>
            {PREPARACION_PASOS.map((paso) => (
              <div key={paso.titulo} className={styles.prepCard}>
                <div className={styles.prepCardIcon}>{paso.icono}</div>
                <h3 className={styles.prepCardTitle}>{paso.titulo}</h3>
                <p className={styles.prepCardDesc}>{paso.descripcion}</p>
              </div>
            ))}
          </div>
          <div className={styles.prepNote}>
            <strong>Nota:</strong> algunas determinaciones tienen requisitos especiales. Ante cualquier duda consultá con tu médico o comunicate con el laboratorio antes de concurrir.
          </div>
        </div>
      </section>

      {/* ── Listado de Análisis ── */}
      <section id="analisis" className={styles.section}>
        <div className={styles.sectionInner}>
          <h2 className={styles.sectionTitle}>Listado de análisis</h2>
          <p className={styles.sectionSubtitle}>
            Realizamos una amplia variedad de análisis clínicos. Si el estudio que buscás no aparece, consultanos.
          </p>
          <div className={styles.analisisGrid}>
            {ANALISIS_CATEGORIAS.map((cat) => (
              <div key={cat.nombre} className={styles.analisisCard}>
                <div className={styles.analisisCardHeader}>
                  <span className={styles.analisisCardIcon}>{cat.icono}</span>
                  <h3 className={styles.analisisCardTitle}>{cat.nombre}</h3>
                </div>
                <ul className={styles.analisisList}>
                  {cat.items.map((item) => (
                    <li key={item} className={styles.analisisItem}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className={styles.footer}>
        <div className={styles.footerInner}>
          <div className={styles.footerBrand}>
            <div>
              <p className={styles.footerName}>Laboratorio Sagrado Corazón</p>
              <p className={styles.footerAddress}>Roque Saenz Peña 1377, Barrio Cofico, Córdoba</p>
            </div>
          </div>
          <div className={styles.footerContact}>
            <a href="tel:+543517686490" className={styles.footerLink}>351-7686490</a>
            <a href="mailto:labsagradocorazon1967@gmail.com" className={styles.footerLink}>labsagradocorazon1967@gmail.com</a>
          </div>
          <p className={styles.footerCopy}>© {new Date().getFullYear()} Laboratorio Sagrado Corazón. Todos los derechos reservados.</p>
        </div>
      </footer>
    </div>
  );
}
